/**
 * A failed request.
 *
 * `status` 0 means the request never reached a server (network error, CORS refusal, timeout) —
 * the same convention Angular's HttpErrorResponse uses, which the customer portal's lead tracking
 * relies on (`HTTP_<status>` vs `NETWORK`). `message` is for logs; never show it to a user.
 *
 * Unlike the Angular error interceptor, failures are never rewritten into an `Error` with an
 * English sentence: the status and the body survive, so a screen can tell a 403 from a 404 and
 * word it through its own translations.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/**
 * The HTTP status a failed call carried, or null when it is not an HTTP failure.
 *
 * Structural rather than `instanceof ApiError`, so it also recognises errors from a client the
 * caller injected (an Error with a numeric `status`, 0 meaning the request never reached a server).
 */
export function errorStatus(error: unknown): number | null {
  if (error instanceof Error && 'status' in error) {
    const status = (error as Error & { status: unknown }).status;
    return typeof status === 'number' ? status : null;
  }
  return null;
}
